import { createContext, useContext } from "react";

import type {
  AppDataState,
  CreateMemorialPayload,
  CreateRSVPPayload,
  Event,
  EventAttendeeRow,
  FamilyToken,
  FuneralHome,
  ImportCandidate,
  ImportResult,
  MonitoringConfig,
  WalkInPayload,
} from "../types/domain";

export interface AppContextValue {
  data: AppDataState;
  ready: boolean;
  staffSessionActive: boolean;
  publicCopy: typeof import("../data/seed").publicCopy;
  setLanguage: (code: AppDataState["publicLanguage"]) => void;
  logout: () => Promise<void>;
  getMemorial: (memorialId: string) => AppDataState["memorials"][number] | undefined;
  getEvent: (eventId: string) => AppDataState["events"][number] | undefined;
  getMemorialEvents: (memorialId: string) => Event[];
  getEventRows: (eventId: string) => EventAttendeeRow[];
  getRecentArrivals: (eventId: string) => EventAttendeeRow[];
  createRSVP: (eventId: string, payload: CreateRSVPPayload) => { attendeeId: string };
  toggleCheckIn: (eventId: string, attendeeId: string) => { duplicate: boolean; checkInId?: string };
  addWalkIn: (eventId: string, payload: WalkInPayload) => { attendeeId: string };
  createMemorialWithEvents: (payload: CreateMemorialPayload) => { memorialId: string };
  completeOnboarding: (funeralHome: FuneralHome) => void;
  runImport: () => Promise<ImportResult>;
  confirmImport: () => void;
  markNotificationRead: (notificationId: string) => void;
  markAllNotificationsRead: () => void;
  notificationError: string;
  notificationsLoading: boolean;
  refreshNotifications: () => Promise<void>;
  unreadNotificationCount: number;
  startMonitoring: () => void;
  stopMonitoring: () => void;
  checkNow: () => boolean;
  updateMonitoringConfig: (updates: Partial<MonitoringConfig>) => void;
  updateMemorialNotes: (memorialId: string, updates: { editableNotes?: string; heroQuote?: string }) => void;
  updateEventNotes: (eventId: string, updates: { specialInstructions?: string; parkingNotes?: string; dressNotes?: string }) => void;
  generateFamilyLink: (memorialId: string, familyName: string) => { token: string; url: string };
  getFamilySession: (token: string) => { memorial: AppDataState["memorials"][number]; events: Event[]; rsvpCount: number; familyToken: FamilyToken } | null;
  getMemorialByShortSlug: (shortSlug: string) => AppDataState["memorials"][number] | undefined;
  syncExistingMemorial: (memorialId: string, candidate: ImportCandidate, changeDescription?: string) => { applied: boolean; changeCount: number };
  getLastSyncTime: (memorialId: string) => string | null;
}

export const AppContext = createContext<AppContextValue | null>(null);

export function useAppStore() {
  const context = useContext(AppContext);
  if (!context) throw new Error("useAppStore must be used inside an app store provider");
  return context;
}
